import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Button,
  TextField,
  MenuItem,
  Grid,
  Pagination,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  TrendingUp as TrendingUpIcon,
  PersonAdd as PersonAddIcon,
  Delete as DisposeIcon,
  SwapHoriz as TransferIcon,
  LocalHospital as WardIcon,
  Tune as AdjustmentIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { ItemInventory, TRANSACTION_TYPES, TransactionTypeConfig } from '../types/inventory.types';
import { inventoryApi } from '../api/inventoryApi';

const InventoryList: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  
  const [inventoryTransactions, setInventoryTransactions] = useState<ItemInventory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    fromLocationId: '',
    toLocationId: ''
  });
  const [showTypeDialog, setShowTypeDialog] = useState<boolean>(false);

  // Icon mapping for transaction types
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'INCOMING': return <TrendingUpIcon />;
      case 'DISPENSED_TO_PATIENT': return <PersonAddIcon />;
      case 'DISPOSED': return <DisposeIcon />;
      case 'TRANSFER': return <TransferIcon />;
      case 'IN_WARD': return <WardIcon />;
      default: return <TrendingUpIcon />;
    }
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT': return 'warning';
      case 'APPROVED': return 'success';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  // Get type color
  const getTypeColor = (type: string) => {
    const config = TRANSACTION_TYPES.find(t => t.type === type);
    return config?.color || 'default';
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Load inventory transactions
  const loadInventoryTransactions = async () => {
    try {
      setLoading(true);
      const params: any = {
        page,
        limit: 10,
        ...filters,
        includeDetails: false
      };
      
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '') {
          delete params[key];
        }
      });

      const data = await inventoryApi.getInventoryTransactions(params);
      setInventoryTransactions(data);
      
      // Calculate total pages (assuming backend returns total count)
      // For now, we'll use a simple calculation
      setTotalPages(Math.ceil(data.length / 10) || 1);
    } catch (error) {
      console.error('Error loading inventory transactions:', error);
      enqueueSnackbar('Failed to load inventory transactions', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle page change
  const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  // Handle filter change
  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(1); // Reset to first page when filtering
  };

  // Handle view transaction
  const handleView = (id: string) => {
    navigate(`${id}`);
  };

  // Handle edit transaction
  const handleEdit = (id: string) => {
    navigate(`${id}/edit`);
  };

  // Handle delete transaction
  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this transaction?')) {
      try {
        await inventoryApi.deleteInventoryTransaction(id);
        enqueueSnackbar('Transaction deleted successfully', { variant: 'success' });
        loadInventoryTransactions();
      } catch (error) {
        console.error('Error deleting transaction:', error);
        enqueueSnackbar('Failed to delete transaction', { variant: 'error' });
      }
    }
  };

  // Handle create new transaction
  const handleCreateTransaction = (type: TransactionTypeConfig) => {
    setShowTypeDialog(false);
    navigate(`new?type=${type.type}`);
  };

  // Load data on component mount and when filters change
  useEffect(() => {
    loadInventoryTransactions();
  }, [page, filters]);

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Inventory Transactions</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowTypeDialog(true)}
          >
            New Transaction
          </Button>
          <IconButton onClick={loadInventoryTransactions} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Transaction Type"
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              size="small"
            >
              <MenuItem value="">All Types</MenuItem>
              {TRANSACTION_TYPES.map((type) => (
                <MenuItem key={type.type} value={type.type}>
                  {type.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Status"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              size="small"
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="DRAFT">Draft</MenuItem>
              <MenuItem value="APPROVED">Approved</MenuItem>
              <MenuItem value="CANCELLED">Cancelled</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper variant="outlined">
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Transaction #</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>From Location</TableCell>
                <TableCell>To Location</TableCell>
                <TableCell align="right">Total Value</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : inventoryTransactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="textSecondary">
                      No inventory transactions found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                inventoryTransactions.map((transaction) => (
                  <TableRow key={transaction.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {transaction.transactionNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(transaction.transactionDate)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getTypeIcon(transaction.type)}
                        label={TRANSACTION_TYPES.find(t => t.type === transaction.type)?.label || transaction.type}
                        color={getTypeColor(transaction.type) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {transaction.fromLocation?.name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {transaction.toLocation?.name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        {transaction.totalStockValue}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={transaction.status}
                        color={getStatusColor(transaction.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={1}>
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            onClick={() => handleView(transaction.id)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        {transaction.status === 'DRAFT' && (
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(transaction.id)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {transaction.status === 'DRAFT' && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(transaction.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {!loading && inventoryTransactions.length > 0 && (
          <Box display="flex" justifyContent="center" p={2}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}
      </Paper>

      {/* Transaction Type Selection Dialog */}
      <Dialog
        open={showTypeDialog}
        onClose={() => setShowTypeDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Transaction Type</DialogTitle>
        <DialogContent>
          <List>
            {TRANSACTION_TYPES
              .filter(type => type.type !== 'IN_WARD' && type.type !== 'ADJUSTMENT' && type.type !== 'DISPENSED_TO_PATIENT') // Hide IN_WARD and ADJUSTMENT types
              .map((type) => (
                <ListItem key={type.type} disablePadding>
                  <ListItemButton onClick={() => handleCreateTransaction(type)}>
                    <ListItemIcon>
                      {getTypeIcon(type.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={type.label}
                      secondary={type.description}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTypeDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryList;
